import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { useAuth } from "@/hooks/useAuth";
import logoImage from "@/components/utils/CRM Logo.png";

interface AuthAnimationProps {
  isOpen: boolean;
  type: "login" | "logout" | "loading";
  onComplete?: () => void;
}

export function AuthAnimation({
  isOpen,
  type,
  onComplete,
}: AuthAnimationProps) {
  const { company } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  
  const primaryColor = company?.primary_color || "#3b82f6";
  const secondaryColor = company?.secondary_color || "#8b5cf6";

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        if (type === "logout") {
          // Para logout, no hacemos fade out porque mostraría el dashboard.
          // Ejecutamos onComplete directo para forzar la redirección al login.
          onComplete?.();
        } else {
          setIsVisible(false);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, onComplete, type]);

  const getContent = () => {
    switch (type) {
      case "login":
        return {
          title: "¡Bienvenido!",
          subtitle: "Redirigiendo a tu espacio de trabajo...",
        };
      case "logout":
        return {
          title: "Cerrando sesión",
          subtitle: "Hasta pronto...",
        };
      case "loading":
        return {
          title: "Procesando...",
          subtitle: "Por favor espera",
        };
      default:
        return {
          title: "Cargando...",
          subtitle: "",
        };
    }
  };

  const content = getContent();

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {isVisible && (
        <>
          {/* Envolvemos el fondo en un contenedor con z-index alto y contexto de apilamiento 
              para asegurar que cubra el dashboard al cerrar sesión */}
          <motion.div 
            className="fixed inset-0 z-[60] bg-slate-50 dark:bg-slate-950"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Forzamos absolute y z-0 para que se renderice DENTRO de este contenedor z-60 y cubra el dashboard */}
            <AnimatedBackground className="absolute z-0" />
          </motion.div>

          <motion.div
            className="fixed inset-0 z-[70] flex flex-col items-center justify-center min-h-screen px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Logo del CRM */}
            {company?.logo_url && (
              <motion.div
                className="-mb-6"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  delay: 0.2,
                  type: "spring",
                  stiffness: 200,
                }}
              >
                <div className="w-40 h-40 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-inner">
                  <img
                    src={company.logo_url}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain drop-shadow-xl"
                  />
                </div>
              </motion.div>
            )}

            {/* Título principal */}
            <motion.h1
              className="text-4xl md:text-5xl font-black mb-2 text-center"
              style={{
                color: primaryColor,
              }}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {content.title}
            </motion.h1>

            {/* Subtítulo */}
            {content.subtitle && (
              <motion.p
                className="text-xl md:text-2xl text-gray-700 text-center max-w-md"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {content.subtitle}
              </motion.p>
            )}

            {/* Indicador de carga */}
            <motion.div
              className="mt-10 w-64 h-1 bg-gray-200 rounded-full overflow-hidden"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 256, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <motion.div
                className="h-full"
                style={{ background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})` }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{
                  duration: 2,
                  ease: "linear",
                }}
              />
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
